// #region IMPORT
import type { BoxProps } from '@mui/material/Box';
import type { CSSObject } from '@mui/material/styles';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';
import type { CarouselOptions, CarouselThumbsProps } from 'src/components/carousel';

import { Children, forwardRef, isValidElement } from 'react';

import ButtonBase from '@mui/material/ButtonBase';
import { Box, Stack, styled, Typography } from '@mui/material';

import { BRAND_COLOR } from 'src/theme/core';

import { Iconify } from 'src/components/iconify';
import { GradientBg } from 'src/components/logo/gradient-bg';
import { CarouselSlide, carouselClasses } from 'src/components/carousel';
// #endregion

type StyledProps = Pick<CarouselOptions, 'axis' | 'slideSpacing'> & BoxProps;

const StyledRootCustom = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'axis',
})<StyledProps>(({ axis }) => ({
  margin: 'auto',
  maxWidth: '100%',
  // thêm overflow: hidden nếu dùng ở chỗ khác nhé
  position: 'relative',
  ...(axis === 'y' && {
    height: '100%',
  }),
}));

const StyledContainerCustom = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'axis' && prop !== 'slideSpacing',
})<StyledProps>(({ axis, slideSpacing }) => ({
  display: 'flex',
  backfaceVisibility: 'hidden',
  ...(axis === 'x' && {
    touchAction: 'pan-y pinch-zoom',
    marginLeft: `calc(${slideSpacing} * -1)`,
  }),
  ...(axis === 'y' && {
    height: '100%',
    flexDirection: 'column',
    touchAction: 'pan-x pinch-zoom',
    marginTop: `calc(${slideSpacing} * -1)`,
  }),
}));

export const CarouselThumbsCustom = forwardRef<HTMLDivElement, BoxProps & CarouselThumbsProps>(
  ({ children, slotProps, options, sx, className, ...other }, ref) => {
    const axis = options?.axis ?? 'x';

    const slideSpacing = options?.slideSpacing ?? '16px';

    const maskStyles = useMaskStyle(axis);

    const renderChildren = Children.map(children, (child) => {
      if (isValidElement(child)) {
        const reactChild = child as React.ReactElement<{ key?: React.Key }>;

        return (
          <CarouselSlide
            key={reactChild.key}
            options={{ ...options, slideSpacing }}
            sx={{ ...slotProps?.slide }}
          >
            {child}
          </CarouselSlide>
        );
      }
      return null;
    });

    return (
      <StyledRootCustom
        position="relative"
        ref={ref}
        axis={axis}
        className={carouselClasses.thumbs.concat(className ? ` ${className}` : '')}
        sx={{
          flexShrink: 0,
          ...(axis === 'x' && { p: 0.5, maxWidth: 1 }),
          ...(axis === 'y' && { p: 0.5, maxHeight: 1 }),
          ...(!slotProps?.disableMask && maskStyles),
          ...sx,
        }}
        {...other}
      >
        <StyledContainerCustom
          component="ul"
          axis={axis}
          slideSpacing={slideSpacing}
          className={carouselClasses.thumbContainer}
          sx={{
            ...slotProps?.container,
          }}
        >
          {renderChildren}
        </StyledContainerCustom>
      </StyledRootCustom>
    );
  }
);

// ----------------------------------------------------------------------

const Icon = (icon: string, size: number) => <Iconify icon={icon} width={size} height={size} />;

type CarouselThumbCustomProps = {
  icon: string;
  title: string;
  isSmUp: boolean;
  selected: boolean;
  index: number;
};

export function CarouselThumbCustom({
  sx,
  icon,
  title,
  isSmUp,
  selected,
  index,
  className,
  ...other
}: ButtonBaseProps & CarouselThumbCustomProps) {
  return (
    <Box position="relative">
      {selected && (
        <Box
          position="absolute"
          top="50%"
          left="35%"
          sx={{
            transform: 'translate(-50%, -50%) scale(2)',
            width: '100%',
            height: '100%',
            zIndex: -1,
            WebkitMaskImage: `
      linear-gradient(to right, transparent, black 40%, black 85%, transparent),
      linear-gradient(to bottom, transparent, black 40%, black 85%, transparent)
    `,
            maskImage: `
      linear-gradient(to right, transparent, black 40%, black 85%, transparent),
      linear-gradient(to bottom, transparent, black 40%, black 85%, transparent)
    `,
            WebkitMaskComposite: 'intersect',
            maskComposite: 'intersect',
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
          }}
        >
          <GradientBg />
        </Box>
      )}
      <ButtonBase
        className={carouselClasses.thumb.concat(className ? ` ${className}` : '')}
        sx={{
          flexShrink: 0,
          cursor: 'pointer',
          borderRadius: 1.25,
          zIndex: 999,
          ...(!selected && {
            boxShadow: (theme) => `0 0 0 1.5px ${theme.vars.palette.divider}`,
          }),
          transition: (theme) =>
            theme.transitions.create(['opacity', 'box-shadow'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.short,
            }),
          ...(selected && {
            opacity: 1,
            zIndex: 0,
            '::before': {
              content: '""',
              position: 'absolute',
              inset: '-2px',
              borderRadius: 'inherit',
              padding: '2px',
              background: BRAND_COLOR,
              WebkitMask: 'linear-gradient(white 0 0) content-box, linear-gradient(white 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              zIndex: 99,
            },
          }),
          ...sx,
        }}
        {...other}
      >
        <Stack
          sx={{
            background: (theme) => theme.palette.background.paper,
            px: isSmUp ? 2.5 : 1,
            py: isSmUp ? 1.5 : 1,
            borderRadius: 1.2,
          }}
        >
          <Stack
            sx={{ opacity: selected ? 1 : 0.48 }}
            alignItems="center"
            spacing={isSmUp ? 1 : 0.5}
          >
            {Icon(icon, isSmUp ? 35 : 24)}
            <Typography
              variant={isSmUp ? 'subtitle1' : 'subtitle2'}
              sx={{
                width: '100%',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
          </Stack>
        </Stack>
      </ButtonBase>
    </Box>
  );
}

// ----------------------------------------------------------------------

function useMaskStyle(axis: CarouselOptions['axis']): CSSObject {
  const baseStyles = {
    zIndex: 9,
    content: '""',
    position: 'absolute',
  };

  if (axis === 'y') {
    return {
      '&::before, &::after': {
        ...baseStyles,
        left: 0,
        height: 40,
        width: '100%',
      },
    };
  }

  return {
    '&::before, &::after': {
      ...baseStyles,
      top: 0,
      width: 40,
      height: '100%',
    },
  };
}
